// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewLiveFindings.test.tsx
import { Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { type StreamingVerifiedFinding } from "../../hooks/useAskPunttStream";

/**
 * One live finding, joined to the comment it has become.
 *
 * `commentId` is the join and the whole point: the row is drawn from the socket
 * frame (warhol publishes the moment its evidence verifier accepts the
 * finding), and the comment is written by christo a poll later. Before that
 * write the row has nothing to open — the finding is real but there is no
 * comment to judge yet — so it is drawn as a plain row rather than a dead link.
 */
export type LiveFindingRow = {
  /** warhol's `intent_id`: what the stream entry and the saved comment share. */
  intentId: string;
  /** warhol's own severity ladder, `critical` | `major` | `minor` | `check`. */
  severity: string;
  description: string;
  /** The saved comment's `_id`, once the provisional review carries one. */
  commentId?: string;
};

/**
 * Urgent first, joined by intent id.
 *
 * Three ranks, not four: `critical` and `major` are one group — warhol's own
 * ladder calls both "cannot ship without this" (`isRequired` in christo's
 * `warholReview.ts`) — and the rail's live list draws them the same way, so the
 * two views of one list do not read in different orders.
 */
const RANK: Record<string, number> = {
  critical: 3,
  major: 2,
  minor: 1,
  check: 0,
};

function descriptionOf(finding: unknown): string {
  if (typeof finding !== "object" || finding === null) return "";
  const record = finding as Record<string, unknown>;
  const words =
    typeof record.observation === "string"
      ? record.observation
      : typeof record.required_action === "string"
        ? record.required_action
        : "";
  return words.replace(/\s+/g, " ").trim();
}

export function liveFindingRows(
  findings: readonly StreamingVerifiedFinding[] | undefined,
  comments: readonly { _id: string; intentId?: string }[] | undefined,
): LiveFindingRow[] {
  const entries = findings ?? [];
  if (entries.length === 0) return [];

  const commentIdByIntent = new Map<string, string>();
  for (const comment of comments ?? []) {
    if (comment?.intentId && !commentIdByIntent.has(comment.intentId)) {
      commentIdByIntent.set(comment.intentId, comment._id);
    }
  }

  const rows: LiveFindingRow[] = [];
  const seen = new Set<string>();
  for (const entry of entries) {
    const intentId = entry?.intentId;
    if (!intentId || seen.has(intentId)) continue;
    const description = descriptionOf(entry.finding);
    if (!description) continue;
    seen.add(intentId);
    const commentId = commentIdByIntent.get(intentId);
    rows.push({
      intentId,
      severity: entry.severity ?? "",
      description,
      ...(commentId ? { commentId } : {}),
    });
  }

  return rows
    .map((row, index) => ({ row, index }))
    .sort(
      (left, right) =>
        (RANK[right.row.severity] ?? -1) - (RANK[left.row.severity] ?? -1) ||
        left.index - right.index,
    )
    .map((entry) => entry.row);
}

/**
 * What the review has found so far, above the comments it will become.
 *
 * The review opens on the provisional document christo writes while the turn
 * runs, so the comments she can judge are already there — but they are only the
 * ones warhol has PUBLISHED, and the gap between "verified" and "written" is
 * one poll. This list closes it: the finding she just watched appear is on
 * screen, and the moment its comment lands the row becomes the way to it.
 *
 * The copy is the rail's (`askV2.rail.foundSoFar` and friends) on purpose. It is
 * the same list, the same words, drawn in the two places she can be looking;
 * a second set of keys would be two strings to keep in step and the first
 * place they would drift is the language nobody on the team reads.
 */
export function AskV2ReviewLiveFindings({
  findings,
  comments,
  onOpenComment,
}: {
  findings: readonly StreamingVerifiedFinding[] | undefined;
  comments: readonly { _id: string; intentId?: string }[] | undefined;
  onOpenComment: (commentId: string) => void;
}) {
  const { t } = useTranslation("ui");
  const rows = liveFindingRows(findings, comments);
  if (rows.length === 0) return null;

  const confidence = (severity: string) =>
    severity === "critical" || severity === "major"
      ? t("highConfidence")
      : severity === "minor"
        ? t("mediumConfidence")
        : t("lowConfidence");

  return (
    <div
      className="mx-4 mt-3"
      data-testid="ask-v2-review-found-so-far"
      role="status"
      aria-live="polite"
    >
      <Text
        as="p"
        size="1"
        weight="medium"
        color="gray"
        data-testid="ask-v2-review-found-so-far-title"
      >
        {t("askV2.rail.foundSoFar")}
      </Text>
      <ul className="mt-1.5 flex flex-col gap-1.5">
        {rows.map((row) => {
          const label = t("askV2.rail.findingAria", {
            severity: confidence(row.severity),
            description: row.description,
          });
          const body = (
            <>
              <Text as="span" size="1" weight="medium" color="gray">
                {confidence(row.severity)}
              </Text>
              <span className="min-w-0 leading-snug">{row.description}</span>
            </>
          );
          return (
            <li
              key={row.intentId}
              className="flex items-start gap-2 text-[12px] text-puntt-neutral-gray-12"
              data-testid={`ask-v2-review-live-finding-${row.intentId}`}
            >
              {row.commentId ? (
                <button
                  type="button"
                  className="flex items-start gap-2 rounded-sm text-left outline-none hover:underline focus-visible:ring-1 focus-visible:ring-puntt-accent-8"
                  aria-label={t("askV2.rail.findingOpenAria", {
                    description: row.description,
                  })}
                  data-testid={`ask-v2-review-live-finding-link-${row.intentId}`}
                  onClick={() => onOpenComment(row.commentId!)}
                >
                  {body}
                </button>
              ) : (
                <span className="flex items-start gap-2" aria-label={label}>
                  {body}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
